import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton } from "@material-ui/core";
import DOMPurify from "dompurify";
import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Route } from "react-router-dom";
import styled from "styled-components/macro";

import { setCurrentLivestockSaleId } from "actions";

import FailedRequestModal from "components/FailedRequestModal";
import MultipleTabsModal from "components/MultipleTabsModal";
import { HashRoute } from "components/HashRoute";
import { Row } from "components/Layout";
import LoadingSpinner from "components/LoadingSpinner";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import MessageBox from "components/MessageBox";
import { PendingChangesModal } from "components/PendingChanges";
import { DefaultSidebarContent } from "components/Sidebar/DefaultSidebarContent";
import { ResponsiveSideBar } from "components/Sidebar/ResponsiveSideBar";

import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { Settings as SettingsEnum } from "constants/settings";
import { userTypes } from "constants/users";

import { AnalyticsHome } from "containers/Analytics";
import { NLISPanel } from "containers/Settings/NLIS";

import {
  getAnalyticsRoute,
  getDashboardRoute,
  getLivestockSaleId,
  getLivestockRoute,
  getLoadsRoute,
  getManageBusinessRoute,
  getMoneyTableRoute,
  getReportJobList,
  getSettingsRoute,
} from "lib/navigation";
import { formatISO8601DateString } from "lib/timeFormats";
import toast from "lib/toast";

import {
  getGlobalAnnouncement,
  getHasEnteredNlisCreds,
  getIsFetchingAuth,
  getIsOnline,
  getIsScaleOperator,
  getSales,
  getSetting,
  selectIsUserOfType,
} from "selectors";

import { useIsFullScreenWithRouteCheck } from "hooks";
import { useOpenCreateSaleModal } from "hooks/useOpenCreateSaleModal";
import { useHasSelectedMobileView } from "hooks/useScreenSize";

import SaleyardHeader from "./SaleyardHeader";
import { SaleyardRoutes } from "./SaleyardRoutes";

const sanitizer = DOMPurify.sanitize;

const CAN_ADD_SALE_USER_TYPES = [
  userTypes.LIVESTOCK_AGENT,
  userTypes.SALEYARD_ADMIN,
  userTypes.SCALE_OPERATOR,
];

const TRANSPORTER_USER_TYPES = [userTypes.TRANSPORTER];

export const SaleyardHome = () => {
  const openCreateSaleModal = useOpenCreateSaleModal();
  const nlisRequired = useSelector(getIsScaleOperator);
  const nlisProvided = useSelector(getHasEnteredNlisCreds);
  const isOnline = useSelector(getIsOnline);
  const sales = useSelector(getSales);
  const stickySale = useSelector(getSetting(SettingsEnum.stickySale));
  const isMobile = useHasSelectedMobileView();

  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isNotificationsBarVisible, setIsNotificationsBarVisible] =
    useState(true);

  const canUserAddSale = useSelector(
    selectIsUserOfType(CAN_ADD_SALE_USER_TYPES),
  );

  const isTransporter = useSelector(selectIsUserOfType(TRANSPORTER_USER_TYPES));

  // TODO - should this check roles, etc?
  const isAuthFetching = useSelector(getIsFetchingAuth);

  const globalAnnouncement = useSelector(getGlobalAnnouncement);

  useEffect(() => {
    // if globalAnnouncement changes - make sure to show the message
    if (globalAnnouncement) {
      setIsNotificationsBarVisible(true);
    }
  }, [globalAnnouncement]);

  const saleId = getLivestockSaleId();
  const dispatch = useDispatch();

  const goToSale = useCallback(
    saleId => {
      dispatch(setCurrentLivestockSaleId(saleId, true, false));
    },
    [dispatch],
  );

  const newSale = useCallback(() => {
    if (isOnline) {
      openCreateSaleModal();
    } else {
      toast.info("Cannot create new sale in offline mode.");
    }
  }, [isOnline, openCreateSaleModal]);

  const isFullScreen = useIsFullScreenWithRouteCheck();

  const loadedSaleId = useMemo(() => {
    //  Ensure this remains in place changes on a livestock sale can cause a whole tree re-render.
    const today = formatISO8601DateString(new Date());
    return stickySale && stickySale.stickySetAt === today
      ? sales[stickySale.livestockSaleId]?.id
      : null;
  }, [sales, stickySale]);

  if (isAuthFetching) {
    return (
      <MessageBox>
        <LoadingSpinner size={24} />
      </MessageBox>
    );
  }

  const nlisUpdateNeeded = nlisRequired && !nlisProvided && !isFullScreen;

  const showSaleSelector =
    ![
      getSettingsRoute(),
      getLivestockRoute(),
      getManageBusinessRoute(),
      getDashboardRoute(),
      getMoneyTableRoute(),
      getReportJobList(),
      getAnalyticsRoute(),
      getLoadsRoute(),
    ].some(page => window.location.pathname.startsWith(page)) && !isTransporter;

  const hasNoSales = isEmpty(sales);

  function toggleSidebar() {
    setIsSidebarVisible(!isSidebarVisible);
  }

  const allowAddSale = !nlisUpdateNeeded && canUserAddSale;

  return (
    <>
      <Row fullHeight>
        {!isFullScreen && (
          <ResponsiveSideBar
            isOpen={isSidebarVisible}
            setIsOpen={setIsSidebarVisible}
            ContentElement={DefaultSidebarContent}
          />
        )}
        <MainWrapper id="main-wrapper">
          {globalAnnouncement && isNotificationsBarVisible && (
            <Row padding={2} justifyBetween alignCenter>
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizer(globalAnnouncement),
                }}
              />
              <div>
                <IconButton>
                  <FontAwesomeIcon
                    icon={faTimes}
                    onClick={() => setIsNotificationsBarVisible(false)}
                  />
                </IconButton>
              </div>
            </Row>
          )}
          {!isFullScreen && (
            <SaleyardHeader
              isMobile={isMobile}
              onClickHamburgerMenu={toggleSidebar}
              showSaleSelector={showSaleSelector}
              newSale={allowAddSale && newSale}
              goToSale={goToSale}
              saleId={+saleId}
            />
          )}
          <WaitForSync
            requiredData={[ApiModel.DEPLOYMENTS, ApiModel.SALEYARDS]}
          >
            <>
              {nlisUpdateNeeded ? (
                <NLISPanel />
              ) : (
                <>
                  {/* Always render the Analytics route i.e. outside of a <Switch> to prevent
                 the component from un-mounting and causing the iframe to re-load */}
                  <Route exact path={getAnalyticsRoute()}>
                    {({ match }) => <AnalyticsHome isMatch={match} />}
                  </Route>
                  <SaleyardRoutes
                    loadedSaleId={loadedSaleId}
                    hasNoSales={hasNoSales}
                  />
                </>
              )}
            </>
          </WaitForSync>
          <HashRoute
            component={PendingChangesModal}
            hash={ModalTypes.PendingChanges}
          />
        </MainWrapper>
      </Row>
      <FailedRequestModal />
      <MultipleTabsModal />
    </>
  );
};

const MainWrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
`;
